import { z } from "zod";
import { emailSchema, validatePhoneForCountry, getPhoneValidationExample, getPhoneLength } from "./common";

// Comprehensive subscriber registration schema
export const subscriberRegistrationSchema = z.object({
  email: emailSchema,

  username: z
    .string()
    .min(1, "Username is required")
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be less than 30 characters")
    .regex(/^[a-zA-Z0-9]+$/, "Username can only contain letters and numbers")
    .refine(
      (val) => (val.match(/[a-zA-Z]/g) || []).length >= 3,
      "Username must contain at least 3 alphabetic characters"
    ),

  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/\d/, "Password must contain at least one number")
    .regex(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character"),

  confirmPassword: z
    .string()
    .min(1, "Please confirm your password"),

  phoneCountry: z
    .string()
    .optional()
    .default("+1"),

  phoneCountryCode: z
    .string()
    .optional()
    .default("US"), // ISO country code for validation

  phoneNumber: z
    .string()
    .optional(),

  city: z
    .string()
    .optional(),

  state: z
    .string()
    .optional(),

  country: z
    .string()
    .optional(),

  termsAccepted: z
    .boolean()
    .refine((val) => val === true, "You must agree to the terms and conditions"),

  // Selected interests validation
  selectedInterests: z
    .array(z.string())
    .min(0, "Interests are optional")
    .refine(
      (interests) => interests.every(interest => interest.trim().length > 0),
      "All interests must be valid and not empty"
    ),
})
.refine(
  (data) => data.password === data.confirmPassword,
  {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  }
)
.superRefine((data, ctx) => {
  // Validate phone number against the country code (optional for subscribers)
  const countryCode = data.phoneCountryCode || 'US';
  const phoneNumber = data.phoneNumber ? data.phoneNumber.trim() : '';
  
  // Phone number is optional for subscribers
  if (!phoneNumber) {
    return; // No validation error if phone number is not provided
  }
  
  // Validate with the country-specific rules (phone number is optional)
  const isValid = validatePhoneForCountry(phoneNumber, countryCode, false);
  
  if (!isValid) {
    const example = getPhoneValidationExample(countryCode);
    const requiredLength = getPhoneLength(countryCode);
    const currentLength = phoneNumber.replace(/\D/g, '').length;
    
    let message = `Invalid phone number for ${countryCode}.`;
    
    if (currentLength === 0) {
      message = `Phone number format is incorrect. Expected ${requiredLength} digits. Example: ${example}`;
    } else if (currentLength < requiredLength) {
      message = `Phone number is too short. You entered ${currentLength} digit${currentLength !== 1 ? 's' : ''}, but ${requiredLength} digit${requiredLength !== 1 ? 's are' : ' is'} required. Example: ${example}`;
    } else if (currentLength > requiredLength) {
      message = `Phone number is too long. You entered ${currentLength} digit${currentLength !== 1 ? 's' : ''}, but only ${requiredLength} digit${requiredLength !== 1 ? 's are' : ' is'} required. Example: ${example}`;
    } else {
      message = `Invalid phone number format. Please use the correct format: ${example}`;
    }
    
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ["phoneNumber"],
    });
  }
});
